import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import type {components} from '@/lib/api-schema'
import type {SoporteDesignation} from '@/lib/sgsst-readiness'

export type DesignationList = components['schemas']['DesignationListRead']
export type Designation = components['schemas']['DesignationRead']

// Comités: COPASST (1.1.6) y convivencia (1.1.8).
export const COMMITTEE_STANDARDS = new Set(['1.1.6', '1.1.8'])
// Responsable del SG-SST (Res. 0312/2019 art. 3).
export const RESPONSABLE_STANDARD = '1.1.1'

/** El vencimiento se deriva, nunca se guarda: «confirmar vigencia» no revive una vencida. */
export function isExpired(designation: Designation): boolean {
  if (!designation.term_end) return false
  // nosemgrep: browser-clock-business-date -- expiry determination off the browser clock; tracked debt, not fixed in this wave (FE-02)
  const now = new Date()
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  return designation.term_end < today
}

/** Las archivadas nunca cuentan; una 1.1.1 sin carta tampoco (regla de la lib). */
export function toSoporteDesignations(items: Designation[]): SoporteDesignation[] {
  return items
    .filter((designation) => designation.archived_at === null)
    .map((designation) => ({
      standardCode: designation.standard_code,
      hasCarta: !!designation.carta,
      expired: isExpired(designation),
    }))
}

export function useDesignations(businessId: string | undefined, year?: number) {
  return useQuery({
    queryKey: ['designations', businessId, year ?? 'current'],
    queryFn: async () =>
      (
        await api.get<DesignationList>(`/businesses/${businessId}/sgsst/designations`, {
          params: year ? {year} : undefined,
        })
      ).data,
    enabled: !!businessId,
  })
}

const DESIGNACIONES_PAGE_SIZE = 10

export function useEmployeeDesignations(businessId: string | undefined, employeeId: string | null, page = 1) {
  return usePagedList<DesignationList>(
    ['designations', businessId, 'employee', employeeId],
    businessId && employeeId ? `/businesses/${businessId}/employees/${employeeId}/designations` : undefined,
    {page, search: ''},
    DESIGNACIONES_PAGE_SIZE
  )
}

function useDesignationInvalidation(businessId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['designations', businessId]})
    // Una designación 1.1.1 mueve las capabilities de la empresa, de donde cuelga el banner
    // «sin SST».
    queryClient.invalidateQueries({queryKey: ['business', businessId]})
  }
}

export type DesignationInput = {
  standard_code: string
  employee_id: string
  party: 'empleador' | 'trabajadores' | null
  rol: 'principal' | 'suplente' | null
  // null = «rige desde hoy», con el reloj del backend.
  effective_date: string | null
  carta_evidence_id: string | null
}

export function useCreateDesignation(businessId: string) {
  const invalidate = useDesignationInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: DesignationInput) => api.post(`/businesses/${businessId}/sgsst/designations`, input),
    onSuccess: invalidate,
  })
}

export function useRemoveDesignation(businessId: string) {
  const invalidate = useDesignationInvalidation(businessId)
  return useMutation({
    mutationFn: async (designationId: string) =>
      api.delete(`/businesses/${businessId}/sgsst/designations/${designationId}`),
    onSuccess: invalidate,
  })
}

export function useConfirmDesignation(businessId: string) {
  const invalidate = useDesignationInvalidation(businessId)
  return useMutation({
    mutationFn: async (designationId: string) =>
      api.post(`/businesses/${businessId}/sgsst/designations/${designationId}/confirm`),
    onSuccess: invalidate,
  })
}
